import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Share2,
  Facebook,
  Twitter,
  Link,
  MessageCircle,
  Send,
  Copy,
  Check,
  X,
} from "lucide-react";

// Helper to track share
const trackShare = async (shareType, resourceId, network) => {
  const token = localStorage.getItem("ea_token");
  if (!token) return; // Only track for logged in users
  
  try {
    await axios.post(
      `${API}/volunteer/share`,
      { type: shareType, resource_id: resourceId, network },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch {
    // Silently fail - don't block the share action
  }
};

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Telegram icon component
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const SOCIAL_NETWORKS = [
  {
    id: "whatsapp",
    name: "WhatsApp",
    icon: WhatsAppIcon,
    color: "#25D366",
    getUrl: (text, url) => `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`,
  },
  {
    id: "facebook",
    name: "Facebook",
    icon: Facebook,
    color: "#1877F2",
    getUrl: (text, url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  {
    id: "twitter",
    name: "X (Twitter)",
    icon: Twitter,
    color: "#000000",
    getUrl: (text, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    id: "telegram",
    name: "Telegram",
    icon: TelegramIcon,
    color: "#0088CC",
    getUrl: (text, url) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
];

// Brutalist style share button for Admin panels
export function ShareButtonBrutalist({ 
  title, 
  text, 
  url, 
  className = "",
  onShare,
  shareType,
  resourceId,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const B = {
    border: "1px solid rgba(45,53,97,0.08)",
    shadow: "none",
    shadowSm: "none",
    mono: "'IBM Plex Mono', monospace",
  };

  const handleShare = async (network) => {
    const shareUrl = network.getUrl(text, url);
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=400");
    
    if (shareType && resourceId) {
      trackShare(shareType, resourceId, network.id);
    }
    
    onShare?.(network.id);
    toast.success(`¡Compartido en ${network.name}!`);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("¡Enlace copiado!");
      
      if (shareType && resourceId) {
        trackShare(shareType, resourceId, "copy");
      }
      
      onShare?.("copy");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#686af315] ${className}`}
        style={{ border: "2px solid #686af3" }}
        title="Compartir en redes"
        data-testid="share-brutalist-btn"
      >
        <Share2 className="w-4 h-4" style={{ color: "#686af3" }} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm p-0" style={{ borderRadius: 0, border: B.border, boxShadow: B.shadow }}>
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "'Instrument Serif', Georgia, serif", color: "#29294d" }}>
              <Share2 className="w-5 h-5" style={{ color: "#686af3" }} />
              COMPARTIR EN REDES
            </DialogTitle>
          </DialogHeader>

          <div className="p-4">
            {/* Preview */}
            <div className="p-3 mb-4" style={{ background: "#f0eee9", border: "2px solid #29294d" }}>
              <p className="text-sm line-clamp-3" style={{ color: "#29294d" }}>{text}</p>
              <p className="text-[10px] mt-2 truncate" style={{ fontFamily: B.mono, color: "#686af3" }}>{url}</p>
            </div>

            {/* Social buttons grid */}
            <div className="grid grid-cols-2 gap-2">
              {SOCIAL_NETWORKS.map((network) => {
                const Icon = network.icon;
                return (
                  <button
                    key={network.id}
                    onClick={() => handleShare(network)}
                    className="flex items-center gap-2 p-2 transition-all hover:-translate-y-0.5"
                    style={{ background: "white", border: `2px solid ${network.color}`, boxShadow: `3px 3px 0px ${network.color}` }}
                    data-testid={`share-brutalist-${network.id}`}
                  >
                    <Icon className="w-5 h-5" style={{ color: network.color }} />
                    <span className="text-xs font-bold uppercase" style={{ fontFamily: B.mono, color: network.color }}>{network.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Copy link button */}
            <button
              onClick={handleCopyLink}
              className="w-full mt-3 flex items-center justify-center gap-2 p-2 transition-all hover:-translate-y-0.5"
              style={{ background: copied ? "#81801a" : "#29294d", border: "2px solid #29294d", boxShadow: "3px 3px 0px #29294d", color: "#fff" }}
              data-testid="share-brutalist-copy"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase" style={{ fontFamily: B.mono }}>¡COPIADO!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase" style={{ fontFamily: B.mono }}>COPIAR ENLACE</span>
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Inline share button that opens a dialog
export function ShareButton({ 
  title, 
  text, 
  url, 
  className = "",
  variant = "default",
  size = "default",
  showLabel = true,
  onShare,
  shareType,
  resourceId,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = async (network) => {
    const shareUrl = network.getUrl(text, url);
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=400");
    
    // Track the share
    if (shareType && resourceId) {
      trackShare(shareType, resourceId, network.id);
    }
    
    onShare?.(network.id);
    toast.success(`Compartiendo en ${network.name}`);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Enlace copiado al portapapeles");
      
      // Track the copy
      if (shareType && resourceId) {
        trackShare(shareType, resourceId, "copy");
      }
      
      onShare?.("copy");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        onShare?.("native");
      } catch {
        // User cancelled
      }
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant={variant}
        size={size}
        className={className}
        data-testid="share-btn"
      >
        <Share2 className="w-4 h-4" />
        {showLabel && <span className="ml-2">Compartir</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[var(--coral)]" />
              Compartir en redes
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {/* Preview */}
            <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-100">
              <p className="font-body text-sm text-gray-700 line-clamp-3">{text}</p>
              <p className="font-body text-xs text-[var(--blue-vibrant)] mt-2 truncate">{url}</p>
            </div>

            {/* Social buttons grid */}
            <div className="grid grid-cols-2 gap-3">
              {SOCIAL_NETWORKS.map((network) => {
                const Icon = network.icon;
                return (
                  <button
                    key={network.id}
                    onClick={() => handleShare(network)}
                    className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all group"
                    data-testid={`share-${network.id}-btn`}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${network.color}15` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: network.color }} />
                    </div>
                    <span className="font-body text-sm text-gray-700">{network.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Copy link button */}
            <button
              onClick={handleCopyLink}
              className="w-full mt-4 flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-[var(--blue-vibrant)] hover:bg-[var(--blue-vibrant)]/5 transition-all"
              data-testid="share-copy-link-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="font-body text-sm text-green-600">¡Copiado!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 text-gray-500" />
                  <span className="font-body text-sm text-gray-600">Copiar enlace</span>
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Direct share buttons row (no dialog)
export function ShareButtonsRow({ 
  text, 
  url, 
  onShare,
  size = "sm",
  className = "",
  shareType,
  resourceId,
}) {
  const handleShare = async (network) => {
    const shareUrl = network.getUrl(text, url);
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=400");
    
    // Track the share
    if (shareType && resourceId) {
      trackShare(shareType, resourceId, network.id);
    }
    
    onShare?.(network.id);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Enlace copiado");
      
      // Track the copy
      if (shareType && resourceId) {
        trackShare(shareType, resourceId, "copy");
      }
      
      onShare?.("copy");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const iconSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";
  const btnSize = size === "sm" ? "w-8 h-8" : "w-10 h-10";

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="share-buttons-row">
      {SOCIAL_NETWORKS.map((network) => {
        const Icon = network.icon;
        return (
          <button
            key={network.id}
            onClick={() => handleShare(network)}
            className={`${btnSize} rounded-full flex items-center justify-center transition-all hover:scale-110 hover:shadow-md`}
            style={{ backgroundColor: network.color }}
            title={`Compartir en ${network.name}`}
            data-testid={`share-row-${network.id}-btn`}
          >
            <Icon className={`${iconSize} text-white`} />
          </button>
        );
      })}
      <button
        onClick={handleCopyLink}
        className={`${btnSize} rounded-full flex items-center justify-center bg-gray-200 hover:bg-gray-300 transition-all hover:scale-110`}
        title="Copiar enlace"
        data-testid="share-row-copy-btn"
      >
        <Copy className={`${iconSize} text-gray-600`} />
      </button>
    </div>
  );
}

// Quick share for emergencies
export function ShareEmergencyButton({ emergency, className = "", brutalist = false }) {
  const url = `${window.location.origin}/needs?emergency=${emergency.emergency_id}`;
  const text = `EMERGENCIA: ${emergency.title}\n${emergency.location_name || 'España'}\n\n${emergency.description || 'Se necesita ayuda urgente.'}\n\n#EmergenciaAnimal #Animales`;

  if (brutalist) {
    return (
      <ShareButtonBrutalist
        title={`Emergencia: ${emergency.title}`}
        text={text}
        url={url}
        className={className}
        shareType="emergency"
        resourceId={emergency.emergency_id}
      />
    );
  }

  return (
    <ShareButton
      title={`Emergencia: ${emergency.title}`}
      text={text}
      url={url}
      className={className}
      variant="outline"
      shareType="emergency"
      resourceId={emergency.emergency_id}
    />
  );
}

// Quick share for lost pets
export function ShareLostPetButton({ pet, className = "", variant = "default" }) {
  const url = `${window.location.origin}/lost-pets?pet=${pet.pet_id}`;
  const speciesLabel = pet.species === "perro" ? "Perro" : pet.species === "gato" ? "Gato" : "Animal";
  const text = `SE BUSCA: ${pet.name}\n${speciesLabel}${pet.breed ? ` - ${pet.breed}` : ''}\n${pet.lost_location_name}\nPerdido el ${new Date(pet.lost_date).toLocaleDateString('es-ES')}\nContacto: ${pet.contact_phone}\n\n¡Ayúdanos a encontrarlo!\n#EmergenciaAnimal #Animales`;

  return (
    <ShareButton
      title={`Se busca: ${pet.name}`}
      text={text}
      url={url}
      className={className}
      variant={variant}
      shareType="lost_pet"
      resourceId={pet.pet_id}
    />
  );
}

// Quick share for needs
export function ShareNeedButton({ need, emergency, className = "" }) {
  const url = `${window.location.origin}/needs?need=${need.need_id}`;
  const urgencyLabel = need.urgency === "urgente" ? "URGENTE - " : need.urgency === "prioridad" ? "PRIORIDAD - " : "";
  const typeLabel = {
    material: "Material",
    voluntarios: "Voluntarios",
    transporte: "Transporte",
    acogida: "Acogida",
    veterinario: "Veterinario",
  }[need.need_type] || "Ayuda";

  const text = `${urgencyLabel}SE NECESITA: ${typeLabel}\n${need.location_name || emergency?.location_name || 'España'}\n\n${need.title}\n${need.description || ''}\n\n¡Tu ayuda puede marcar la diferencia!\n#EmergenciaAnimal #Animales`;

  return (
    <ShareButton
      title={need.title}
      text={text}
      url={url}
      className={className}
      variant="ghost"
      size="sm"
      shareType="need"
      resourceId={need.need_id}
    />
  );
}
